import React from "react";

import { Form, Formik } from "formik";
import Grid from "@material-ui/core/Grid";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import {
  BusinessFieldWithFilter,
  RenderBusinessOptionWithDetail,
} from "components/Form/Fields";
import { Button, SecondaryButton } from "components/Form";
import { Typography } from "@material-ui/core";
import {
  BusinessFieldFilterOptions,
  DefaultBusinessFieldFilterOptions,
} from "constants/businesses";

interface MergeBusinessDialogProps {
  isOpen: boolean;
  handleSubmit: (object) => void;
  handleClose: () => void;
  readOnly: boolean;
  targetBusiness: Business;
  onBusinessChange: (any) => void;
  errorMessage?: string;
}

interface MergeBusinessFormProps {
  handleClose: () => void;
  readOnly: boolean;
  targetBusiness: Business;
  onBusinessChange: (any) => void;
  errorMessage?: string;
}

function MergeBusinessForm({
  handleClose,
  readOnly,
  targetBusiness,
  onBusinessChange,
  errorMessage,
}: MergeBusinessFormProps) {
  return (
    <Form data-tour="mergeBusinessForm">
      <DialogTitle
        onClose={handleClose}
      >{`Merge Businesses into "${targetBusiness.name}"`}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <p>
              {"Merging will move information from the selected business into " +
                `"${targetBusiness.name}". If there's new information that doesn't exist ` +
                `already for "${targetBusiness.name}", it will be added.`}
            </p>
            <p>
              Once a Merge has happened you’ll see the business selected within
              the “Merged Businesses” section, where you can undo this merge at
              any time.
            </p>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BusinessFieldWithFilter
              name="sourceBusinessId"
              label="Source Business (to be deactivated)"
              disabled={readOnly}
              allowAddNew={false}
              renderOption={RenderBusinessOptionWithDetail}
              renderOptionHeadings
              globalBusinesses={false}
              onChangeExtra={onBusinessChange}
              excludeIds={[targetBusiness.id]}
              searchFields={[
                ...DefaultBusinessFieldFilterOptions,
                BusinessFieldFilterOptions.abn,
                BusinessFieldFilterOptions.primaryContactEmail,
              ]}
            />
          </Grid>
        </Grid>
        {errorMessage && (
          <Typography color="error">
            <h3>
              Businesses could not be merged due to the following problems:
            </h3>
            <ul>
              {errorMessage.split("\n").map(e => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton data-tour="cancel" onClick={handleClose} type="button">
          Cancel
        </SecondaryButton>
        <Button
          data-tour="merge-business"
          type="submit"
          disabled={readOnly || errorMessage}
        >
          Merge Business
        </Button>
      </DialogActions>
    </Form>
  );
}

export const MergeBusinessDialog = ({
  isOpen,
  handleSubmit,
  handleClose,
  readOnly,
  targetBusiness,
  onBusinessChange,
  errorMessage,
}: MergeBusinessDialogProps) => (
  <Dialog open={isOpen} maxWidth="md" fullWidth onClose={handleClose}>
    <Formik
      initialValues={{ sourceBusinessId: null }}
      onSubmit={!readOnly && handleSubmit}
    >
      <MergeBusinessForm
        handleClose={handleClose}
        readOnly={readOnly}
        targetBusiness={targetBusiness}
        onBusinessChange={onBusinessChange}
        errorMessage={errorMessage}
      />
    </Formik>
  </Dialog>
);
